/**
 * Lot Details Modal Controller
 *
 * Manages the comprehensive lot details modal with full information display
 * @module portal/modules/ui/modals/lotDetailsModal
 */

import { state } from '../../state';
import { Lot } from '../../types';
import {
  escapeHtml,
  escapeAttr,
  fmtDate,
  renderEstimateSourceBadge,
  getSourceDisplay,
} from '../../formatting';
import {
  extractZipOrPostalCode,
  calculateDistance,
  getPreferredDistanceUnits,
} from '../../distance';
import { collectLotImages, __isArchived } from '../../image';
import { els } from '../elements';
import { captureFocus, restoreFocus } from './focusManager';

const bodyEl = document.body;

/**
 * Check if the lot details modal is currently open
 */
export function isLotDetailsModalOpen(): boolean {
  return !!(els.lotDetailsModal && !els.lotDetailsModal.hidden);
}

/**
 * Open the lot details modal with comprehensive lot information
 * @param lot - The lot to display details for
 */
export function openLotDetailsModal(lot: Lot): void {
  if (!lot) return;

  captureFocus('lastActiveBeforeLotDetailsModal');

  if (els.lotDetailsModalTitle) {
    const lotTitle = lot.title || 'Lot Details';
    els.lotDetailsModalTitle.textContent = lotTitle;
  }

  if (els.lotDetailsModalContent) {
    let html = '<div class="lot-details-grid">';

    html += '<div class="lot-details-section"><strong>Basic Information</strong></div>';
    html += '<div class="lot-details-label">Title:</div>';
    html += `<div class="lot-details-value">${escapeHtml(lot.title || '-')}</div>`;

    if (lot.notes) {
      html += '<div class="lot-details-label">Description:</div>';
      html += `<div class="lot-details-value">${escapeHtml(lot.notes)}</div>`;
    }

    html += '<div class="lot-details-label">Source:</div>';
    html += `<div class="lot-details-value">${getSourceDisplay(lot.source, 'source-logo-details')}</div>`;

    if (lot.url) {
      html += '<div class="lot-details-label">Lot URL:</div>';
      html += `<div class="lot-details-value"><a href="${escapeAttr(lot.url)}" target="_blank" rel="noopener">Open Lot Page</a></div>`;
    }

    html += '<div class="lot-details-section"><strong>Auction Information</strong></div>';

    if (lot.auctionTitle) {
      html += '<div class="lot-details-label">Auction Title:</div>';
      html += `<div class="lot-details-value">${escapeHtml(lot.auctionTitle)}</div>`;
    }

    html += '<div class="lot-details-label">Auction ID:</div>';
    html += `<div class="lot-details-value">${escapeHtml(lot.auctionId || '-')}</div>`;

    html += '<div class="lot-details-label">Lot Number:</div>';
    html += `<div class="lot-details-value">${escapeHtml(lot.lotNumber || '-')}</div>`;

    if (lot.itemNumber) {
      html += '<div class="lot-details-label">Item Number:</div>';
      html += `<div class="lot-details-value">${escapeHtml(lot.itemNumber)}</div>`;
    }

    if (lot.auctioneer) {
      html += '<div class="lot-details-label">Auctioneer:</div>';
      if (lot.auctioneerLink) {
        html += `<div class="lot-details-value"><a href="${escapeAttr(lot.auctioneerLink)}" target="_blank" rel="noopener">${escapeHtml(lot.auctioneer)}</a></div>`;
      } else {
        html += `<div class="lot-details-value">${escapeHtml(lot.auctioneer)}</div>`;
      }
    }

    if (lot.closeAt) {
      html += '<div class="lot-details-label">Close Date:</div>';
      html += `<div class="lot-details-value">${escapeHtml(fmtDate(lot.closeAt))}</div>`;
    }

    html += '<div class="lot-details-section"><strong>Pricing</strong></div>';

    if (lot.currentBid != null || lot.currentBidRaw) {
      html += '<div class="lot-details-label">Current Bid:</div>';
      let bidDisplay = '-';
      if (lot.currentBid != null) {
        const bidAmt = Number(lot.currentBid);
        bidDisplay = Number.isFinite(bidAmt)
          ? `$${bidAmt.toFixed(2)}`
          : `$${String(lot.currentBid)}`;
      } else if (lot.currentBidRaw) {
        bidDisplay = escapeHtml(lot.currentBidRaw);
      }
      html += `<div class="lot-details-value">${bidDisplay}</div>`;
    }

    if (lot.nextRequiredBid != null || lot.nextRequiredBidRaw) {
      html += '<div class="lot-details-label">Next Bid:</div>';
      let nextBidDisplay = '-';
      if (lot.nextRequiredBid != null) {
        const nextBidAmt = Number(lot.nextRequiredBid);
        nextBidDisplay = Number.isFinite(nextBidAmt)
          ? `$${nextBidAmt.toFixed(2)}`
          : `$${String(lot.nextRequiredBid)}`;
      } else if (lot.nextRequiredBidRaw) {
        nextBidDisplay = escapeHtml(String(lot.nextRequiredBidRaw));
      }
      html += `<div class="lot-details-value">${nextBidDisplay}</div>`;
    }

    if (lot.lowSellValue != null || lot.lowSellValueRaw) {
      html += '<div class="lot-details-label">Low Estimate:</div>';
      let lowEstDisplay = '-';
      if (lot.lowSellValue != null) {
        const lowAmt = Number(lot.lowSellValue);
        lowEstDisplay = Number.isFinite(lowAmt)
          ? `$${lowAmt.toFixed(2)}`
          : `$${String(lot.lowSellValue)}`;
      } else if (lot.lowSellValueRaw) {
        lowEstDisplay = escapeHtml(String(lot.lowSellValueRaw));
      }
      const estimateBadge = renderEstimateSourceBadge(
        lot.estimateSource as { isShared?: boolean } | null | undefined
      );
      html += `<div class="lot-details-value">${lowEstDisplay}${estimateBadge}</div>`;

      if (lot.lowListings?.length) {
        html += '<div class="lot-details-label">Low Price Examples:</div>';
        html += '<div class="lot-details-value">';
        html += '<ul class="lot-details-list">';
        for (const item of lot.lowListings) {
          const itemStr = String(item ?? '');
          html += `<li><a href="${escapeHtml(itemStr)}" target="_blank" rel="noopener noreferrer">${escapeHtml(itemStr)}</a></li>`;
        }
        html += '</ul>';
        html += '</div>';
      }
    } else if (lot.lowListings?.length) {
      html += '<div class="lot-details-label">Low Price Examples:</div>';
      html += '<div class="lot-details-value">';
      html += '<ul class="lot-details-list">';
      for (const item of lot.lowListings) {
        const itemStr = String(item ?? '');
        html += `<li><a href="${escapeHtml(itemStr)}" target="_blank" rel="noopener noreferrer">${escapeHtml(itemStr)}</a></li>`;
      }
      html += '</ul>';
      html += '</div>';
    }

    if (lot.highSellValue != null || lot.highSellValueRaw) {
      html += '<div class="lot-details-label">High Estimate:</div>';
      let highEstDisplay = '-';
      if (lot.highSellValue != null) {
        const highAmt = Number(lot.highSellValue);
        highEstDisplay = Number.isFinite(highAmt)
          ? `$${highAmt.toFixed(2)}`
          : `$${String(lot.highSellValue)}`;
      } else if (lot.highSellValueRaw) {
        highEstDisplay = escapeHtml(String(lot.highSellValueRaw));
      }
      const estimateBadge = renderEstimateSourceBadge(
        lot.estimateSource as { isShared?: boolean } | null | undefined
      );
      html += `<div class="lot-details-value">${highEstDisplay}${estimateBadge}</div>`;

      if (lot.highListings?.length) {
        html += '<div class="lot-details-label">High Price Examples:</div>';
        html += '<div class="lot-details-value">';
        html += '<ul class="lot-details-list">';
        for (const item of lot.highListings) {
          const itemStr = String(item ?? '');
          html += `<li><a href="${escapeHtml(itemStr)}" target="_blank" rel="noopener noreferrer">${escapeHtml(itemStr)}</a></li>`;
        }
        html += '</ul>';
        html += '</div>';
      }
    } else if (lot.highListings?.length) {
      html += '<div class="lot-details-label">High Price Examples:</div>';
      html += '<div class="lot-details-value">';
      html += '<ul class="lot-details-list">';
      for (const item of lot.highListings) {
        const itemStr = String(item ?? '');
        html += `<li><a href="${escapeHtml(itemStr)}" target="_blank" rel="noopener noreferrer">${escapeHtml(itemStr)}</a></li>`;
      }
      html += '</ul>';
      html += '</div>';
    }

    html += '<div class="lot-details-section"><strong>Location & Shipping</strong></div>';

    html += '<div class="lot-details-label">Location:</div>';
    html += `<div class="lot-details-value">${escapeHtml(lot.location || '-')}</div>`;
    html += '<div class="lot-details-value" id="distance-from-zip"></div>';
    setTimeout(() => {
      try {
        const userZip = state?.settings?.zipCode || localStorage.getItem('userZipCode');
        const loc = lot.location || '';
        const lotInfo = extractZipOrPostalCode(loc);
        const el = document.getElementById('distance-from-zip');
        if (userZip && lotInfo && el) {
          el.textContent = 'Calculating distance...';
          calculateDistance(userZip, lotInfo.code)
            .then(miles => {
              if (miles !== null) {
                const pref = getPreferredDistanceUnits(userZip, state.settings || {});
                const displayVal = pref === 'km' ? Math.round(miles * 1.60934) : miles;
                const unit = pref === 'km' ? 'km' : 'miles';
                el.textContent = `Distance: ${displayVal} ${unit}`;
              } else {
                el.textContent = 'Distance: unavailable';
              }
            })
            .catch(() => {
              el.textContent = 'Distance: unavailable';
            });
        }
      } catch {
        // Ignore errors in distance calculation
      }
    }, 0);

    html += '<div class="lot-details-label">Shipping:</div>';
    html += `<div class="lot-details-value">${escapeHtml(lot.shipping || '-')}</div>`;

    const allDetailImages = collectLotImages(lot);
    // Filter out archived images
    const sanitizedDetailImages = allDetailImages.filter(entry => {
      const canonical = entry && (entry.canonicalUrl || entry.displayUrl);
      const display = entry && (entry.displayUrl || entry.canonicalUrl);
      return !__isArchived(canonical || display);
    });
    const imageCount = sanitizedDetailImages.length;
    if (imageCount > 0) {
      html += `<div class="lot-details-section"><strong>Images (${imageCount})</strong></div>`;
      html += '<div class="lot-details-images"><ul class="image-list">';
      for (let i = 0; i < sanitizedDetailImages.length; i++) {
        const entry = sanitizedDetailImages[i];
        if (!entry) continue;
        const canonical = entry.canonicalUrl || entry.displayUrl;
        const displaySrc = entry.displayUrl || entry.canonicalUrl || '';
        const altText = `${lot.title || 'Lot '} image ${i + 1}`;
        const ariaLabel = `View image ${i + 1}`;
        html += '<li>';
        html += `<button type="button" class="image-tile" aria-label="${escapeAttr(ariaLabel)}">`;
        const lotIdAttr = lot.id ? ` data-lot-id="${escapeAttr(lot.id)}"` : '';
        html += `<img src="${escapeAttr(displaySrc)}" alt="${escapeAttr(altText)}" loading="lazy" data-image-url="${escapeAttr(canonical || displaySrc)}"${lotIdAttr} />`;
        html += '</button>';
        html += '</li>';
      }
      html += '</ul></div>';
    }

    html += '<div class="lot-details-section"><strong>Metadata</strong></div>';

    if (lot.createdAt) {
      html += '<div class="lot-details-label">Created:</div>';
      html += `<div class="lot-details-value">${escapeHtml(fmtDate(lot.createdAt))}</div>`;
    }

    if (lot.updatedAt) {
      html += '<div class="lot-details-label">Updated:</div>';
      html += `<div class="lot-details-value">${escapeHtml(fmtDate(lot.updatedAt))}</div>`;
    }

    html += '</div>';
    els.lotDetailsModalContent.innerHTML = html;
  }

  if (els.lotDetailsModalBackdrop) {
    els.lotDetailsModalBackdrop.hidden = false;
  }
  if (els.lotDetailsModal) {
    els.lotDetailsModal.hidden = false;
  }
  if (bodyEl) {
    bodyEl.classList.add('modal-open');
  }

  window.requestAnimationFrame(() => {
    if (els.closeLotDetailsModal) {
      (els.closeLotDetailsModal as HTMLElement).focus();
    }
  });
}

/**
 * Close the lot details modal
 */
export function closeLotDetailsModal(): void {
  if (els.lotDetailsModal) {
    els.lotDetailsModal.hidden = true;
  }
  if (els.lotDetailsModalBackdrop) {
    els.lotDetailsModalBackdrop.hidden = true;
  }
  if (bodyEl) {
    bodyEl.classList.remove('modal-open');
  }

  restoreFocus('lastActiveBeforeLotDetailsModal');
}
